import { Eye } from "lucide-react";
import { avatarColor, initials, type InboxPresencePeer } from "./_shared";

function Avatar({ name, avatarUrl }: { name: string; avatarUrl: string | null }) {
  if (avatarUrl) {
    return (
      <img
        src={avatarUrl}
        alt={name}
        className="h-4 w-4 rounded-full object-cover ring-1 ring-white"
      />
    );
  }
  return (
    <div
      className={`flex h-4 w-4 items-center justify-center rounded-full text-[8px] font-semibold text-white ring-1 ring-white ${avatarColor(name)}`}
    >
      {initials(name)}
    </div>
  );
}

function firstNames(peers: InboxPresencePeer[]): string {
  const names = peers.map((p) => String(p.name ?? "").split(" ")[0]).filter(Boolean);
  if (names.length === 0) return "A teammate";
  if (names.length === 1) return names[0];
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  return `${names[0]}, ${names[1]} and ${names.length - 2} more`;
}

// Subtle inbox-list co-presence badge: shows a small avatar stack plus an eye
// icon when one or more teammates are currently viewing/typing in the
// conversation. Typing takes visual priority over viewing (matches the
// in-conversation PresenceIndicator). Hover title spells out who is present.
export function InboxRowPresence({
  peers,
  memberId,
}: {
  peers: InboxPresencePeer[];
  memberId: number;
}) {
  if (!peers || peers.length === 0) return null;

  const typists = peers.filter((p) => p.state === "typing");
  const shown = typists.length > 0 ? typists : peers;
  const isTyping = typists.length > 0;
  const verb = isTyping
    ? shown.length === 1
      ? "is typing"
      : "are typing"
    : shown.length === 1
    ? "is viewing"
    : "are viewing";
  const title = `${firstNames(shown)} ${verb} this conversation`;

  return (
    <span
      className="flex shrink-0 items-center gap-1 rounded-full bg-gray-100 px-1.5 py-0.5"
      title={title}
      data-testid={`presence-inbox-${memberId}`}
    >
      <Eye className={`h-3 w-3 ${isTyping ? "text-brand" : "text-gray-400"}`} />
      <span className="flex -space-x-1">
        {shown.slice(0, 3).map((p) => (
          <Avatar key={p.actingUserId} name={p.name} avatarUrl={p.avatarUrl} />
        ))}
      </span>
    </span>
  );
}
